// client/src/components/pages/LexiStar/FuelConfig/FuelConfig.jsx
//
// LexiStar fuel configuration (ST-00188 M2-A1; LS-09 §5.12 "norms, prices …
// under Setup"; LS-03 FR-FUEL-001 / FR-FUEL-008; LS-04 §9, §11).
// Settings > Monitoring > LexiStar Fuel Norms & Prices, route
// /settings/modules/monitoring/lexistar-fuel.
//
// Three effective-dated tables: consumption norms (per vehicle class or asset,
// loaded / empty, idle rate, route factors), diesel prices by region and rate
// cards per vehicle class. Rows in force and scheduled are shown by default;
// "Show history" adds superseded ones. Changing a row closes it and opens a new
// one — the history is the record of what every trip was budgeted with.
//
// Permissions: page lexistar.trips.view (planners read the norms — there is no
// lexistar.fuel.view key); add / change lexistar.fuel.manage.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaGasPump,
  FaPlus,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
  FaEdit,
} from 'react-icons/fa';
import PageHeader from '../../../common/PageHeader';
import SegmentedControl from '../../../common/SegmentedControl';
import { useAuth } from '../../../../context/AuthContext';
import { formatAppDateTime } from '../../../../utils/dateUtils';
import {
  listNorms,
  createNorm,
  changeNorm,
  listPrices,
  createPrice,
  changePrice,
  listRateCards,
  createRateCard,
  changeRateCard,
  listVehicleClasses,
} from '../../../../services/lexistarFuel';
import { listAssets } from '../../../../services/lexistarAssets';
import { listRoutes } from '../../../../services/lexistarRoutes';
import FuelRowDialog from './FuelRowDialog';
import { KINDS, STATUS_META, sortNorms, sortByKey, normSubject, formatRand } from './fuelConfig';

const API = {
  norm: { list: listNorms, create: createNorm, change: changeNorm, key: 'norms' },
  price: { list: listPrices, create: createPrice, change: changePrice, key: 'prices' },
  rateCard: {
    list: listRateCards,
    create: createRateCard,
    change: changeRateCard,
    key: 'rate_cards',
  },
};

const TABS = [
  { value: 'norm', label: 'Consumption norms', shortLabel: 'Norms' },
  { value: 'price', label: 'Diesel prices', shortLabel: 'Prices' },
  { value: 'rateCard', label: 'Rate cards', shortLabel: 'Rates' },
];

const th =
  'px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap';
const td = 'px-4 py-3 whitespace-nowrap text-sm';

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.superseded;
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function Period({ row }) {
  return (
    <span className="text-sm text-slate-700 tabular-nums">
      {formatAppDateTime(row.effective_from)}
      <span className="text-slate-400"> → </span>
      {row.effective_to ? (
        formatAppDateTime(row.effective_to)
      ) : (
        <span className="text-slate-500">open</span>
      )}
    </span>
  );
}

export default function FuelConfig() {
  const { hasPermission } = useAuth();
  const canView =
    hasPermission('lexistar.trips.view') ||
    hasPermission('lexistar.fuel.manage') ||
    hasPermission('lexistar.routes.view');
  const canManage = hasPermission('lexistar.fuel.manage');

  const [tab, setTab] = useState('norm');
  const [history, setHistory] = useState(false);
  const [rows, setRows] = useState({ norm: [], price: [], rateCard: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState(null); // { mode, row }
  const [vehicleClasses, setVehicleClasses] = useState(['truck']);
  const [assets, setAssets] = useState([]);
  const [routes, setRoutes] = useState([]);

  const fetchRows = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const params = history ? { include_history: true } : {};
      const [n, p, r] = await Promise.all([
        API.norm.list(params),
        API.price.list(params),
        API.rateCard.list(params),
      ]);
      setRows({ norm: n.norms || [], price: p.prices || [], rateCard: r.rate_cards || [] });
    } catch (err) {
      setError(
        err.response?.status === 503
          ? 'LexiStar fuel configuration is not installed on this server yet.'
          : err.response?.data?.error || 'Failed to load the fuel configuration.'
      );
    } finally {
      setLoading(false);
    }
  }, [canView, history]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Pickers for the dialog; failures only thin them out.
  useEffect(() => {
    if (!canManage) return;
    listVehicleClasses()
      .then((r) => setVehicleClasses(r.vehicle_classes || ['truck']))
      .catch(() => {});
    listAssets({ limit: 200 })
      .then((r) => setAssets(r.assets || []))
      .catch(() => setAssets([]));
    listRoutes({ status: 'approved' })
      .then((r) => setRoutes(r.routes || []))
      .catch(() => setRoutes([]));
  }, [canManage]);

  const sorted = useMemo(
    () => ({
      norm: sortNorms(rows.norm),
      price: sortByKey(rows.price, (r) => r.region),
      rateCard: sortByKey(rows.rateCard, (r) => r.vehicle_class),
    }),
    [rows]
  );

  const counts = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(rows).map(([k, v]) => [k, v.filter((r) => r.status === 'current').length])
      ),
    [rows]
  );

  const submit = async (payload) => {
    const api = API[tab];
    if (dialog.mode === 'create') {
      await api.create(payload);
      setNotice(`${KINDS[tab].label} added.`);
    } else {
      await api.change(dialog.row.id, payload);
      setNotice(
        payload.effective_from
          ? `${KINDS[tab].label} changed from ${formatAppDateTime(payload.effective_from)}.`
          : `${KINDS[tab].label} changed. The previous values stay on record.`
      );
    }
    setDialog(null);
    await fetchRows();
  };

  const header = (
    <PageHeader
      icon={FaGasPump}
      title="Fuel Norms & Prices"
      subtitle="What a trip's litre allowance and cost are worked out from. Changes start a new period; dispatched trips keep theirs."
      actions={
        canView && canManage ? (
          <button
            type="button"
            onClick={() => setDialog({ mode: 'create', row: null })}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 shadow-sm"
          >
            <FaPlus size={12} aria-hidden="true" /> Add {KINDS[tab].label.toLowerCase()}
          </button>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-start">
            <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <p className="font-medium">
              You do not have permission to view the fuel configuration.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const list = sorted[tab];
  const changeButton = (row) =>
    canManage && row.status !== 'superseded' ? (
      <button
        type="button"
        onClick={() => setDialog({ mode: 'change', row })}
        aria-label={`Change ${tab === 'norm' ? `${normSubject(row)} ${row.load_state}` : tab === 'price' ? row.region : row.vehicle_class}`}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
      >
        <FaEdit size={11} aria-hidden="true" /> Change
      </button>
    ) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start">
            <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <p className="flex-1 font-medium">{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
              className="ml-4 text-red-400 hover:text-red-600"
            >
              <FaTimes />
            </button>
          </div>
        )}
        {notice && (
          <div
            role="status"
            className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg mb-6 flex items-start"
          >
            <FaCheckCircle
              className="mr-3 mt-0.5 flex-shrink-0 text-emerald-500"
              aria-hidden="true"
            />
            <p className="flex-1 text-sm font-medium">{notice}</p>
            <button
              type="button"
              onClick={() => setNotice(null)}
              aria-label="Dismiss"
              className="ml-4 text-emerald-400 hover:text-emerald-600"
            >
              <FaTimes />
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <SegmentedControl
            options={TABS.map((t) => ({ ...t, badge: counts[t.value] }))}
            value={tab}
            onChange={setTab}
            ariaLabel="Fuel configuration"
            className="sm:w-[34rem]"
          />
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={history}
              onChange={(e) => setHistory(e.target.checked)}
            />
            Show history
          </label>
        </div>

        <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
          {tab === 'norm' && (
            <p className="px-6 pt-4 text-xs text-slate-500">
              Allowance per leg = planned km × norm ÷ 100 (asset norm if set, else its class; times
              the route factor) + idle allowance. Litres are the unit of record.
            </p>
          )}
          <div className="p-6">
            {loading && list.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
            ) : list.length === 0 ? (
              <div className="text-center py-10">
                <FaGasPump className="mx-auto text-slate-300 mb-3" size={28} aria-hidden="true" />
                <p className="text-sm text-slate-600">
                  {tab === 'norm'
                    ? 'No consumption norms yet. Add loaded and empty norms for your truck class.'
                    : tab === 'price'
                      ? 'No diesel price yet. Add the current price so fuel variances can be valued in rand.'
                      : 'No rate cards yet. Add one per vehicle class to estimate trip cost.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    {tab === 'norm' && (
                      <tr>
                        <th className={th}>Applies to</th>
                        <th className={th}>Load</th>
                        <th className={`${th} text-right`}>L/100 km</th>
                        <th className={`${th} text-right`}>Idle L/h</th>
                        <th className={th}>Route factors</th>
                        <th className={th}>Effective</th>
                        <th className={th}>Status</th>
                        <th className={`${th} text-right`}>Actions</th>
                      </tr>
                    )}
                    {tab === 'price' && (
                      <tr>
                        <th className={th}>Region</th>
                        <th className={`${th} text-right`}>Per litre</th>
                        <th className={th}>Effective</th>
                        <th className={th}>Status</th>
                        <th className={`${th} text-right`}>Actions</th>
                      </tr>
                    )}
                    {tab === 'rateCard' && (
                      <tr>
                        <th className={th}>Vehicle class</th>
                        <th className={`${th} text-right`}>Per km</th>
                        <th className={`${th} text-right`}>Driver per hour</th>
                        <th className={th}>Toll class</th>
                        <th className={th}>Effective</th>
                        <th className={th}>Status</th>
                        <th className={`${th} text-right`}>Actions</th>
                      </tr>
                    )}
                  </thead>
                  <tbody className="bg-surface divide-y divide-slate-200">
                    {list.map((row) => (
                      <tr
                        key={row.id}
                        className={row.status === 'superseded' ? 'text-slate-400' : ''}
                      >
                        {tab === 'norm' && (
                          <>
                            <td className={`${td} font-medium text-slate-900`}>
                              {row.scope === 'asset' ? (
                                <span className="font-mono">{normSubject(row)}</span>
                              ) : (
                                normSubject(row)
                              )}
                            </td>
                            <td className={td}>
                              {row.load_state === 'loaded' ? 'Loaded' : 'Empty'}
                            </td>
                            <td className={`${td} text-right tabular-nums font-medium`}>
                              {Number(row.l_per_100km).toFixed(2)}
                            </td>
                            <td className={`${td} text-right tabular-nums`}>
                              {row.idle_lph == null ? '—' : Number(row.idle_lph).toFixed(2)}
                            </td>
                            <td className={td}>
                              {Object.keys(row.route_factor || {}).length
                                ? `${Object.keys(row.route_factor).length} route${Object.keys(row.route_factor).length === 1 ? '' : 's'}`
                                : '—'}
                            </td>
                          </>
                        )}
                        {tab === 'price' && (
                          <>
                            <td className={`${td} font-medium text-slate-900`}>{row.region}</td>
                            <td className={`${td} text-right tabular-nums font-medium`}>
                              {formatRand(row.price_per_l, 3)}
                            </td>
                          </>
                        )}
                        {tab === 'rateCard' && (
                          <>
                            <td className={`${td} font-medium text-slate-900`}>
                              {row.vehicle_class}
                            </td>
                            <td className={`${td} text-right tabular-nums font-medium`}>
                              {formatRand(row.rate_per_km, 3)}
                            </td>
                            <td className={`${td} text-right tabular-nums`}>
                              {formatRand(row.driver_rate_per_h)}
                            </td>
                            <td className={td}>{row.toll_class || '—'}</td>
                          </>
                        )}
                        <td className={td}>
                          <Period row={row} />
                        </td>
                        <td className={td}>
                          <StatusPill status={row.status} />
                        </td>
                        <td className={`${td} text-right`}>{changeButton(row)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <FuelRowDialog
        isOpen={!!dialog}
        kind={tab}
        mode={dialog?.mode || 'create'}
        row={dialog?.row || null}
        vehicleClasses={vehicleClasses}
        assets={assets}
        routes={routes}
        onClose={() => setDialog(null)}
        onSubmit={submit}
      />
    </div>
  );
}
